import React, { useEffect, useRef, useState } from 'react';
import { ClipboardCopy, FileText, Check, X } from 'lucide-react';
import { formatEntryText, formatEntryMarkdown } from '../../utils/formatEntry';
import { getActiveSystem } from '../../config/system';

/**
 * The two copy controls of #195, beside the Favorite heart on every generic
 * detail page: copy the entry as a plain-text stat block, or as
 * GitHub-flavored Markdown. Formatting is `utils/formatEntry.js` — one
 * registry-driven formatter, so a user-authored Family gets both buttons with
 * no code written for it.
 *
 * The Clipboard API needs a secure context (https / localhost) and can be
 * refused; a copy that silently does nothing is worse than one that says it
 * failed, so the button reports both outcomes: the icon becomes a check
 * ("Copied") or an X ("Copy failed") for two seconds, and an `aria-live`
 * status announces the same for screen readers. Where `navigator.clipboard`
 * is absent the old `execCommand('copy')` textarea fallback is tried first.
 */

async function writeToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // fall through to execCommand
    }
  }
  const holder = document.createElement('textarea');
  try {
    holder.value = text;
    holder.setAttribute('readonly', '');
    holder.style.position = 'fixed';
    holder.style.opacity = '0';
    document.body.appendChild(holder);
    holder.select();
    return document.execCommand('copy');
  } catch (err) {
    return false;
  } finally {
    holder.remove();
  }
}

const RESET_MS = 2000;

/**
 * One copy control: icon button + copied/failed feedback + aria-live status.
 * Exported so siblings of #195 (the tracker export, #336) reuse the exact
 * clipboard path and failure reporting rather than growing a second one.
 */
export function CopyButton({ label, icon: Icon, getText }) {
  const [state, setState] = useState('idle');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = async (e) => {
    // May sit near other interactive chrome — never navigate, never bubble.
    e.preventDefault();
    e.stopPropagation();
    const ok = await writeToClipboard(getText());
    setState(ok ? 'copied' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), RESET_MS);
  };

  const Shown = state === 'copied' ? Check : (state === 'failed' ? X : Icon);
  const color = state === 'copied' ? '#4ade80'
    : (state === 'failed' ? '#f87171' : 'var(--text-muted)');
  const title = state === 'copied' ? 'Copied'
    : (state === 'failed' ? 'Copy failed' : label);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex-shrink-0 p-1 mt-1 transition-transform duration-150 hover:scale-110"
      aria-label={label}
      title={title}
    >
      <Shown className="w-5 h-5" style={{ color }} aria-hidden="true" />
      <span className="sr-only" role="status">
        {state === 'copied' ? 'Copied to clipboard' : (state === 'failed' ? 'Copy failed' : '')}
      </span>
    </button>
  );
}

export default function CopyEntryButtons({ family, entry }) {
  if (!family || !entry) return null;
  const opts = () => ({
    origin: window.location.origin,
    system: getActiveSystem(),
  });
  return (
    <>
      <CopyButton
        label="Copy as text"
        icon={ClipboardCopy}
        getText={() => formatEntryText(family, entry, opts())}
      />
      <CopyButton
        label="Copy as Markdown"
        icon={FileText}
        getText={() => formatEntryMarkdown(family, entry, opts())}
      />
    </>
  );
}
